<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import adminAPI from "@/services/api";
import { ReceiptRefundIcon } from "@heroicons/vue/20/solid";
import { PlaneTakeoff } from "lucide-vue-next";
import {
  InformationCircleIcon,
  DocumentMagnifyingGlassIcon,
  TrashIcon,
  PaperClipIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import type {
  ExpenseClaimDocument,
  ExpenseTypeSchema,
  File,
} from "@/interfaces/data-contracts";
import type { RetrieveClaimWithRelationsResponse, ClaimExpenses } from "@/interfaces";
import { useClaimStore, useNotificationStore } from "@/store";
import BaseButton from "../ui/buttons/BaseButton.vue";
import FilePreviewModal from "../files/FilePreviewModal.vue";
import AlertModal from "@/components/ui/modals/AlertModal.vue";
import AddExpense from "./AddExpense.vue";
import { ExpenseTypesMap } from "@/utils/config/constants";

const emits = defineEmits(["update", "expense-created"]); // Refetch claim

const props = defineProps<{
  claimExpenses: ClaimExpenses;
}>();

const previewedFile = ref<{ data: string; file: File } | null>();
const modalAddExpense = ref(false);

async function deleteExpense(expenseId: number) {
  const { success } = await adminAPI.deleteExpense(expenseId.toString());
  if (success) {
    emits("update");
    useNotificationStore().emitNotification({
      title: "Préjudice supprimé avec succès",
      type: "success",
    });
  }
}

async function previewDocument(file?: File) {
  if (!file) return;
  const data = await adminAPI.retrieveFile(file.id);
  if (data) previewedFile.value = { data, file };
}
const tooltipFile = (document: ExpenseClaimDocument) =>
  "Ajouté le " +
  format(new Date(document.createdAt), "d MMMM 'à' HH'h'mm", { locale: fr });
</script>
<template>
  <AlertModal
    :open="modalAddExpense"
    type="info"
    title="Ajouter une dépense"
    confirm-message=""
    @close="modalAddExpense = false"
    @cancel="modalAddExpense = false"
    @confirm="modalAddExpense = false"
    width="max-w-2xl"
  >
    <AddExpense
      :claim-id="useClaimStore().claim.id"
      :users-claims="useClaimStore().claim.usersClaims"
      :isB2B="
        Boolean(
          useClaimStore().claim.legalSpecification.impactedSegment.reservation
            .travelAgencyCompany
        )
      "
      @expense-created="
        () => {
          emits('update');
          modalAddExpense = false;
        }
      "
    />
  </AlertModal>

  <FilePreviewModal
    v-show="previewedFile?.data"
    @close="previewedFile = null"
    @cancel="previewedFile = null"
    :file="(previewedFile?.file as any)"
    :data="previewedFile?.data"
  />

  <div class="py-6 sm:px-6">
    <slot name="header" />

    <div class="pt-4">
      <ul
        v-if="claimExpenses.length"
        role="list"
        class="w-full col-span-3 bg-white border border-gray-300 divide-y divide-gray-100 rounded-md shadow-sm"
      >
        <li
          v-for="{ expense, ...claimExpense } in claimExpenses"
          class="py-4 pl-4 pr-5 text-sm leading-6"
        >
          <div class="flex items-center justify-between">
            <div
              class="flex items-center flex-1 w-0 cursor-pointer"
              @click="previewDocument(expense.claimDocument?.file)"
            >
              <span class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true">
                <component
                  :is="ExpenseTypesMap[(expense.expenseType as ExpenseTypeSchema).type].icon"
                  :size="20"
                />
              </span>
              <!-- <PlaneTakeoff :size="5" class="flex-shrink-0 w-5 h-5 text-gray-400" /> -->
              <div class="flex flex-1 min-w-0 gap-2 ml-4">
                <span class="font-medium truncate">
                  <span class="">{{
                    expense.expenseName ||
                    expense.claimDocument?.file?.name ||
                    "Dépense supplémentaire"
                  }}</span>

                  <!-- @TODO Badge component -->
                  <span
                    class="inline-flex ml-3 items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
                  >
                    <svg
                      class="h-1.5 w-1.5"
                      :class="expense.claimDocument ? 'fill-blue-500' : 'fill-amber-500'"
                      viewBox="0 0 6 6"
                      aria-hidden="true"
                    >
                      <circle cx="3" cy="3" r="3" />
                    </svg>
                    {{ Number.parseInt(expense.price) / 100 }} {{ expense.currencyCode }}
                  </span>

                  <!-- <span class="ml-3 text-xs text-indigo-600">(
                    + {{ Number.parseInt(expense.price) / 100 }} {{ expense.currencyCode }}
                    <span v-if="!expense?.claimDocument"> · Justificatif manquant</span>
                  )</span> -->
                </span>
              </div>
            </div>
            <div class="flex flex-shrink-0 ml-4 space-x-4">
              <button
                type="button"
                v-if="expense.claimDocument"
                v-tooltip="tooltipFile(expense.claimDocument as any)"
                @click="previewDocument(expense.claimDocument?.file)"
                class="font-medium text-blue-600 rounded-md hover:text-blue-500"
              >
                <DocumentMagnifyingGlassIcon class="w-5 h-5" />
              </button>
              <span v-if="expense.claimDocument" class="text-gray-200" aria-hidden="true"
                >|</span
              >
              <button
                type="button"
                class="font-medium text-gray-900 rounded-md hover:text-gray-800"
                @click="deleteExpense(expense.id)"
              >
                <TrashIcon class="w-5 h-5" />
              </button>
            </div>
          </div>
          <div class="pt-2 text-xs italic pl-9">
            <!-- Quick solution for the first iteration -->
            Payé par
            {{
              claimExpense.userClaimId
                ? claimExpense.paidBy
                : claimExpense.paidBy === "TRAVEL_AGENCY"
                ? "l'agence de voyages"
                : "l'entreprise"
            }}
            <span v-if="!expense?.claimDocument"> · Justificatif manquant</span>
          </div>
        </li>
      </ul>
      <ul
        v-else
        role="list"
        class="w-full col-span-3 mb-5 bg-white border border-gray-300 border-dotted divide-y divide-gray-100 rounded-md shadow-sm"
      >
        <li class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
          <div class="flex items-center flex-1 w-0">
            <InformationCircleIcon
              class="flex-shrink-0 w-5 h-5 ml-2 text-gray-400"
              aria-hidden="true"
            />
            <div class="flex flex-1 min-w-0 gap-2 ml-4">
              <span class="flex items-center font-medium truncate">
                Aucun préjudice pour l'instant
              </span>
              <div class="ml-auto">
                <BaseButton
                  text="Ajouter une dépense"
                  @click="modalAddExpense = !modalAddExpense"
                />
                <!-- <BaseButton v-else text="Fermer" @click="modalAddExpense = false" :icon="XMarkIcon" /> -->
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div v-if="claimExpenses?.length" class="mt-5 text-left">
        <BaseButton
          text="Ajouter une dépense"
          @click="modalAddExpense = !modalAddExpense"
        />
        <!-- <BaseButton v-else text="Fermer" @click="modalAddExpense = false" :icon="XMarkIcon" /> -->
      </div>
    </div>
  </div>
</template>
